/**
 * Visuals Builder Service — dashboards config-driven de "Visuales" (migración 031).
 *
 * Habla con routes/visuals_builder.py. Cada widget tiene dos capas editables:
 *   render_config (JSON) — cómo se construye la gráfica (option ECharts / bloque)
 *   data_sql (texto)     — cómo se jalan los datos (SQL Athena)
 *   data (JSON)          — snapshot materializado que se sirve para pintar
 *
 * Mismo patrón que dashboardsService.js (Oportunidades Comerciales).
 */

const API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

const qs = (params) => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') usp.set(k, v);
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
};

async function request(path, { method = 'GET', body } = {}) {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);

  const res = await fetch(`${API_URL}${path}`, opts);
  let data = null;
  try { data = await res.json(); } catch { /* respuesta vacía o no-JSON */ }

  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

// ---------------------------------------------------------------------------
// Meta
// ---------------------------------------------------------------------------

export const getVisualsMeta = (username = '') =>
  request(`/api/visuals/dashboards/meta${qs({ username })}`);

// ---------------------------------------------------------------------------
// Dashboards
// ---------------------------------------------------------------------------

export const listVisualDashboards = (username) =>
  request(`/api/visuals/dashboards${qs({ username })}`);

export const getDefaultVisualDashboard = (username) =>
  request(`/api/visuals/dashboards/default${qs({ username })}`);

export const getVisualDashboard = (dashboardId, username) =>
  request(`/api/visuals/dashboards/${dashboardId}${qs({ username })}`);

export const createVisualDashboard = ({ username, name, engine, dataset, theme, date_cutoff, is_default = false, with_defaults = false }) =>
  request('/api/visuals/dashboards', {
    method: 'POST',
    body: { username, name, engine, dataset, theme, date_cutoff, is_default, with_defaults },
  });

export const updateVisualDashboard = (dashboardId, { username, name, is_default, columns, engine, dataset, theme, date_cutoff }) =>
  request(`/api/visuals/dashboards/${dashboardId}`, {
    method: 'PATCH',
    body: { username, name, is_default, columns, engine, dataset, theme, date_cutoff },
  });

export const deleteVisualDashboard = (dashboardId, username) =>
  request(`/api/visuals/dashboards/${dashboardId}${qs({ username })}`, { method: 'DELETE' });

export const reorderVisualWidgets = (dashboardId, username, widgetIds) =>
  request(`/api/visuals/dashboards/${dashboardId}/reorder`, {
    method: 'POST',
    body: { username, widget_ids: widgetIds },
  });

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------

export const createVisualWidget = (dashboardId, { username, widget_type, title, render_config, data_sql, size, position }) =>
  request(`/api/visuals/dashboards/${dashboardId}/widgets`, {
    method: 'POST',
    body: { username, widget_type, title, render_config, data_sql, size, position },
  });

export const updateVisualWidget = (dashboardId, widgetId, payload) =>
  request(`/api/visuals/dashboards/${dashboardId}/widgets/${widgetId}`, {
    method: 'PATCH',
    body: payload,
  });

export const deleteVisualWidget = (dashboardId, widgetId, username) =>
  request(`/api/visuals/dashboards/${dashboardId}/widgets/${widgetId}${qs({ username })}`, {
    method: 'DELETE',
  });

// ---------------------------------------------------------------------------
// Ejecución / materialización de data_sql (Fase 4) — solo admin, motor Athena
// ---------------------------------------------------------------------------

// Ejecuta un data_sql y devuelve filas SIN guardar (botón "Probar SQL").
export const runVisualSql = (dashboardId, { username, data_sql }) =>
  request(`/api/visuals/dashboards/${dashboardId}/run-sql`, {
    method: 'POST',
    body: { username, data_sql },
  });

// Materializa 1 widget (ejecuta su data_sql → widget.data).
export const refreshVisualWidget = (dashboardId, widgetId, username) =>
  request(`/api/visuals/dashboards/${dashboardId}/widgets/${widgetId}/refresh`, {
    method: 'POST',
    body: { username },
  });

// Materializa todos los widgets con data_sql del dashboard.
export const refreshVisualDashboard = (dashboardId, username) =>
  request(`/api/visuals/dashboards/${dashboardId}/refresh`, {
    method: 'POST',
    body: { username },
  });

const visualsBuilderService = {
  getVisualsMeta,
  listVisualDashboards,
  getDefaultVisualDashboard,
  getVisualDashboard,
  createVisualDashboard,
  updateVisualDashboard,
  deleteVisualDashboard,
  reorderVisualWidgets,
  createVisualWidget,
  updateVisualWidget,
  deleteVisualWidget,
  runVisualSql,
  refreshVisualWidget,
  refreshVisualDashboard,
};

export default visualsBuilderService;
